import { DragHandleIcon, PlusIcon, TrashIcon } from "@cynthai/icons";
import {
  Button,
  PlainTable,
  PlainTableBody,
  PlainTableCell,
  PlainTableHeader,
  PlainTableHeaderCell,
  PlainTableRow,
  Select,
  Tabs,
  useDragReorder,
} from "@cynthai/ui";
import { memo, useMemo } from "react";
import { tv } from "tailwind-variants";

import {
  type DraftRow,
  duplicateRowKeys,
  STATEMENT_TYPES,
  type StatementType,
  statementLabel,
} from "@/features/classification/lib/statement-tree";
import { m } from "@/paraglide/messages";

const statementTreeRowsEditorStyles = tv({
  slots: {
    container: "min-h-0 min-w-0 w-full",
    section: "flex min-h-0 min-w-0 flex-col gap-3",
    scrollArea:
      "scroll-area relative max-h-[65vh] overflow-auto rounded-lg border border-border",
    table: "w-full min-w-[1080px] overflow-visible",
    cell: "px-2 py-1.5",
    input:
      "h-9 w-full min-w-0 rounded border border-transparent bg-transparent px-2 text-sm text-text-primary outline-none hover:border-border focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-text-tertiary placeholder:italic disabled:cursor-default disabled:hover:border-transparent aria-invalid:border-danger",
    dragHandle:
      "flex size-8 cursor-grab items-center justify-center rounded text-text-tertiary hover:bg-secondary hover:text-text-primary focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-default disabled:opacity-40",
  },
});

const TEXT_COLUMNS = [
  { field: "level_1", label: m.statement_taxonomy_editor_level_1 },
  { field: "level_2", label: m.statement_taxonomy_editor_level_2 },
  { field: "level_3", label: m.statement_taxonomy_editor_level_3 },
] as const;

type RowEditorProps = Readonly<{
  row: DraftRow;
  isReadOnly: boolean;
  isDuplicate: boolean;
  onUpdate: (key: string, patch: Partial<DraftRow>) => void;
}>;

export function StatementTreeRowsEditor({
  rows,
  isReadOnly,
  onAdd,
  onUpdate,
  onRemove,
  onReorder,
}: Readonly<{
  rows: DraftRow[];
  isReadOnly: boolean;
  onAdd: (statementType: StatementType) => void;
  onUpdate: RowEditorProps["onUpdate"];
  onRemove: (key: string) => void;
  onReorder: (fromKey: string, toKey: string) => void;
}>) {
  const styles = statementTreeRowsEditorStyles();

  const statements = useMemo(
    () =>
      STATEMENT_TYPES.map((type) => ({
        type,
        rows: rows.filter((row) => row.statement_type === type),
      })),
    [rows],
  );
  const duplicateKeys = useMemo(() => duplicateRowKeys(rows), [rows]);

  return (
    <Tabs
      className={styles.container()}
      items={statements.map((statement) => ({
        value: statement.type,
        label: statementLabel(statement.type),
        count: statement.rows.length,
        children: (
          <StatementSection
            statementType={statement.type}
            rows={statement.rows}
            duplicateKeys={duplicateKeys}
            isReadOnly={isReadOnly}
            onAdd={() => onAdd(statement.type)}
            onUpdate={onUpdate}
            onRemove={onRemove}
            onReorder={onReorder}
          />
        ),
      }))}
    />
  );
}

function StatementSection({
  statementType,
  rows,
  duplicateKeys,
  isReadOnly,
  onAdd,
  onUpdate,
  onRemove,
  onReorder,
}: Readonly<{
  statementType: StatementType;
  rows: DraftRow[];
  duplicateKeys: ReadonlySet<string>;
  isReadOnly: boolean;
  onAdd: () => void;
  onUpdate: RowEditorProps["onUpdate"];
  onRemove: (key: string) => void;
  onReorder: (fromKey: string, toKey: string) => void;
}>) {
  const keys = useMemo(() => rows.map((row) => row.key), [rows]);

  const { draggedKey, getDragHandleProps, getDropTargetProps } = useDragReorder(
    {
      keys,
      onReorder,
      isDisabled: isReadOnly,
    },
  );

  const styles = statementTreeRowsEditorStyles();

  return (
    <section className={styles.section()}>
      {!isReadOnly && (
        <Button
          size="sm"
          variant="ghost-light-bordered"
          icon={PlusIcon}
          iconSize={16}
          className="self-end"
          onPress={onAdd}
        >
          {m.statement_taxonomy_editor_add_row()}
        </Button>
      )}

      <div className={styles.scrollArea()}>
        <PlainTable sticky className={styles.table()}>
          <caption className="sr-only">{statementLabel(statementType)}</caption>

          <PlainTableHeader>
            <PlainTableRow>
              <PlainTableHeaderCell className="w-12">
                {null}
              </PlainTableHeaderCell>

              <PlainTableHeaderCell className="w-28 px-2">
                {m.statement_taxonomy_editor_code()}
              </PlainTableHeaderCell>

              <PlainTableHeaderCell className="w-64 px-2">
                {m.statement_taxonomy_editor_sign()}
              </PlainTableHeaderCell>

              <PlainTableHeaderCell className="w-56 px-4">
                {m.statement_taxonomy_editor_level_1()}
              </PlainTableHeaderCell>

              <PlainTableHeaderCell className="px-4">
                {m.statement_taxonomy_editor_level_2()}
              </PlainTableHeaderCell>

              <PlainTableHeaderCell className="px-4">
                {m.statement_taxonomy_editor_level_3()}
              </PlainTableHeaderCell>

              {!isReadOnly && (
                <PlainTableHeaderCell className="w-12">
                  {null}
                </PlainTableHeaderCell>
              )}
            </PlainTableRow>
          </PlainTableHeader>

          <PlainTableBody>
            {rows.map((row) => {
              const label =
                row.level_3 || row.level_2 || row.level_1 || statementType;

              return (
                <PlainTableRow
                  key={row.key}
                  isDragging={draggedKey === row.key}
                  className="data-[dragging=true]:bg-primary/10 data-[dragging=true]:opacity-50"
                  {...getDropTargetProps(row.key)}
                >
                  <PlainTableCell className={styles.cell()}>
                    <button
                      type="button"
                      className={styles.dragHandle()}
                      disabled={isReadOnly}
                      aria-label={m.statement_taxonomy_editor_reorder({
                        label,
                      })}
                      {...getDragHandleProps(row.key)}
                    >
                      <DragHandleIcon />
                    </button>
                  </PlainTableCell>

                  <RowFields
                    row={row}
                    isReadOnly={isReadOnly}
                    isDuplicate={duplicateKeys.has(row.key)}
                    onUpdate={onUpdate}
                  />

                  {!isReadOnly && (
                    <PlainTableCell className={styles.cell()}>
                      <Button
                        size="square-sm"
                        variant="ghost"
                        icon={TrashIcon}
                        iconSize={16}
                        aria-label={m.statement_taxonomy_editor_remove_row({
                          label,
                        })}
                        onPress={() => onRemove(row.key)}
                      />
                    </PlainTableCell>
                  )}
                </PlainTableRow>
              );
            })}

            {rows.length === 0 && (
              <PlainTableRow>
                <PlainTableCell
                  colSpan={isReadOnly ? 6 : 7}
                  className="text-text-tertiary py-8 text-center"
                >
                  {m.statement_taxonomy_editor_empty()}
                </PlainTableCell>
              </PlainTableRow>
            )}
          </PlainTableBody>
        </PlainTable>
      </div>
    </section>
  );
}

// Drag state and edits to other rows do not need to render these controls again.
const RowFields = memo(function RowFields({
  row,
  isReadOnly,
  isDuplicate,
  onUpdate,
}: RowEditorProps) {
  const styles = statementTreeRowsEditorStyles();

  return (
    <>
      <PlainTableCell className={styles.cell()}>
        <input
          className={styles.input()}
          aria-label={m.statement_taxonomy_editor_code()}
          disabled={isReadOnly}
          value={row.code ?? ""}
          title={row.code ?? ""}
          placeholder={m.statement_taxonomy_editor_empty_cell()}
          onChange={(event) =>
            onUpdate(row.key, { code: event.target.value || null })
          }
        />
      </PlainTableCell>

      <PlainTableCell className={styles.cell()}>
        <Select
          aria-label={m.statement_taxonomy_editor_sign()}
          isDisabled={isReadOnly}
          value={row.sign === 1 ? "1" : "-1"}
          items={[
            { value: "1", label: m.statement_taxonomy_editor_sign_debit() },
            { value: "-1", label: m.statement_taxonomy_editor_sign_credit() },
          ]}
          onChange={(value) =>
            onUpdate(row.key, { sign: value === "1" ? 1 : -1 })
          }
          buttonProps={{
            color: "secondary",
            size: "sm",
            variant: "ghost-light-bordered",
            className: "w-full justify-between",
          }}
        />
      </PlainTableCell>

      {TEXT_COLUMNS.map((column) => (
        <PlainTableCell key={column.field} className={styles.cell()}>
          <input
            className={styles.input()}
            aria-label={column.label()}
            aria-invalid={isDuplicate || undefined}
            disabled={isReadOnly}
            value={row[column.field] ?? ""}
            title={row[column.field] ?? ""}
            placeholder={m.statement_taxonomy_editor_empty_cell()}
            onChange={(event) =>
              onUpdate(row.key, {
                [column.field]:
                  column.field === "level_3"
                    ? event.target.value || null
                    : event.target.value,
              })
            }
          />
        </PlainTableCell>
      ))}
    </>
  );
});
